import PreviewInContext from '@anm/components/PreviewInContext';
import { DEFAULT_THUMBNAIL_WIDTH } from '@anm/constants/videoLandings';
import { DEFAULT_VIDEO_META } from '@anm/data/hostingDefaults';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { VideoSizes, VideoStatusCode } from '@anm/hosting-player';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import { hostedVideoSelectors, EmbedData } from '../../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../../store/modules/project';

import HighLightEmbedPreview from './HighlightEmbedPreview';
import InlineEmbedPreview from './InlineEmbedPreview';
import TextEmbedPreview from './TextEmbedPreview';
import ThumbnailEmbedPreview from './ThumbnailEmbedPreview';
import { PreviewTitle, PreviewWrapper } from './Wrapper';
import { getActiveEmbedType } from './helpers';
import { useReinitPopover } from './hooks';

type EmbedPreviewProps = {
  hostId: string;
  errorCode?: VideoStatusCode;
  videoName?: string;
  renderInnerContent?: () => JSX.Element;
};

export type EmbedItemProps = EmbedPreviewProps & {
  embedData: EmbedData;
  isNoVideo?: boolean;
};

const EmbedPreview: FC<EmbedPreviewProps> = ({
  hostId,
  errorCode,
  videoName = DEFAULT_VIDEO_META.description,
  renderInnerContent
}) => {
  const embedData = useSelector(hostedVideoSelectors.selectEmbedData);
  const projectSizes = useSelector(projectSelectors.selectCurrentProjectSizes) as VideoSizes;

  useReinitPopover(embedData);

  if (!embedData) return null;

  const isInlineEmbed = embedData.type === 'inline';
  const isTextPopover = embedData.popover.popoverType === 'text';
  const isHighlightPopover = !isInlineEmbed && embedData.popover.popoverType === 'highlight';
  const isThumbnailPopover = !isInlineEmbed && embedData.popover.popoverType === 'thumbnail';

  const ratio = projectSizes.width / projectSizes.height;
  const placeholderHeight = Math.round(DEFAULT_THUMBNAIL_WIDTH / ratio);
  const previewCommonProps = { hostId, embedData, renderInnerContent };
  const isNoVideo = errorCode === VideoStatusCode.no_video;
  const isVertical = ratio <= 1;

  return (
    <>
      {isMobileOrIPad() && <PreviewTitle title="Preview" level={4} />}
      <PreviewWrapper>
        <PreviewInContext
          button={embedData.popover.button}
          contextVariant={getActiveEmbedType(embedData)}
          {...{ isVertical, placeholderHeight }}
        >
          {(() => {
            switch (true) {
              case isInlineEmbed:
                return <InlineEmbedPreview {...{ errorCode }} {...previewCommonProps} />;

              case isTextPopover:
                return <TextEmbedPreview {...previewCommonProps} />;

              case isHighlightPopover:
                return <HighLightEmbedPreview {...{ videoName, isNoVideo }} {...previewCommonProps} />;

              case isThumbnailPopover:
                return <ThumbnailEmbedPreview {...{ isNoVideo, videoName }} {...previewCommonProps} />;
            }
          })()}
        </PreviewInContext>
      </PreviewWrapper>
    </>
  );
};

export default EmbedPreview;
